
import { useNavigate } from "react-router-dom";
import { 
  NavigationMenu,
  NavigationMenuContent,
  NavigationMenuItem,
  NavigationMenuLink,
  NavigationMenuList,
  NavigationMenuTrigger,
} from "@/components/ui/navigation-menu";
import { Github, Home, Calculator, Menu } from "lucide-react";
import { useState } from "react";
import { Sheet, SheetContent, SheetTrigger } from "@/components/ui/sheet";
import { Logo } from "./Logo";

const calculators = [
  { name: "Apple Inc.", symbol: "AAPL" },
  { name: "Johnson & Johnson", symbol: "JNJ" },
  { name: "Tesla Inc.", symbol: "TSLA" },
  { name: "Vanguard S&P 500 ETF", symbol: "VOO" },
  { name: "SPDR S&P 500 ETF", symbol: "SPY" },
  { name: "Vanguard Total Stock Market ETF", symbol: "VTI" },
  { name: "Invesco QQQ Trust", symbol: "QQQ" },
  { name: "iShares Core S&P 500 ETF", symbol: "IVV" },
  { name: "Berkshire Hathaway Inc.", symbol: "BRK.B" },
  { name: "Alphabet Inc.", symbol: "GOOGL" }
];

interface LayoutProps {
  children: React.ReactNode;
}

const Layout = ({ children }: LayoutProps) => {
  const navigate = useNavigate();
  const [isOpen, setIsOpen] = useState(false);

  const NavContent = () => (
    <>
      <NavigationMenuItem>
        <NavigationMenuLink
          className="group inline-flex h-10 w-max items-center justify-center rounded-md bg-background px-4 py-2 text-sm font-medium transition-colors hover:bg-accent hover:text-accent-foreground focus:bg-accent focus:text-accent-foreground focus:outline-none disabled:pointer-events-none disabled:opacity-50 data-[active]:bg-accent/50 data-[state=open]:bg-accent/50"
          onClick={() => navigate('/')}
        >
          <Home className="mr-2 h-4 w-4" />
          Home
        </NavigationMenuLink>
      </NavigationMenuItem>

      <NavigationMenuItem>
        <NavigationMenuTrigger>
          <Calculator className="mr-2 h-4 w-4" />
          Calculators
        </NavigationMenuTrigger>
        <NavigationMenuContent>
          <div className="grid gap-3 p-4 w-[400px] md:w-[500px] lg:w-[600px]">
            <div className="grid grid-cols-2 gap-4">
              {calculators.map((calc) => (
                <button
                  key={calc.symbol}
                  onClick={() => navigate(`/stock/${calc.symbol}`)}
                  className="flex items-center space-x-2 rounded-md p-2 hover:bg-accent text-left"
                >
                  <Calculator className="h-4 w-4" />
                  <div>
                    <div className="text-sm font-medium">{calc.name}</div>
                    <div className="text-xs text-muted-foreground">{calc.symbol}</div>
                  </div>
                </button>
              ))}
            </div>
          </div>
        </NavigationMenuContent>
      </NavigationMenuItem>
    </>
  );

  return (
    <div className="min-h-screen flex flex-col">
      {/* Navigation Menu */}
      <nav className="border-b bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/60">
        <div className="max-w-7xl mx-auto px-4">
          {/* Mobile Menu */}
          <div className="md:hidden flex items-center justify-between h-16">
            <Logo />
            <Sheet open={isOpen} onOpenChange={setIsOpen}>
              <SheetTrigger asChild>
                <button className="p-2">
                  <Menu className="h-6 w-6" />
                </button>
              </SheetTrigger>
              <SheetContent side="left">
                <div className="py-4">
                  <button
                    className="flex items-center space-x-2 w-full p-2 hover:bg-accent rounded-md"
                    onClick={() => {
                      navigate('/');
                      setIsOpen(false);
                    }}
                  >
                    <Home className="h-4 w-4" />
                    <span>Home</span>
                  </button>
                  {calculators.map((calc) => (
                    <button
                      key={calc.symbol}
                      onClick={() => {
                        navigate(`/stock/${calc.symbol}`);
                        setIsOpen(false);
                      }}
                      className="flex items-center space-x-2 w-full p-2 hover:bg-accent rounded-md"
                    >
                      <Calculator className="h-4 w-4" />
                      <div className="text-left">
                        <div className="text-sm font-medium">{calc.name}</div>
                        <div className="text-xs text-muted-foreground">{calc.symbol}</div>
                      </div>
                    </button>
                  ))}
                </div>
              </SheetContent>
            </Sheet>
          </div>

          {/* Desktop Menu */}
          <div className="hidden md:flex md:justify-between md:items-center">
            <Logo />
            <NavigationMenu>
              <NavigationMenuList>
                <NavContent />
              </NavigationMenuList>
            </NavigationMenu>
          </div>
        </div>
      </nav>

      {/* Main Content */}
      <main className="flex-grow">
        {children}
      </main>

      {/* Footer */}
      <footer className="border-t bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/60">
        <div className="max-w-7xl mx-auto px-4 py-6">
          <div className="flex flex-col md:flex-row justify-between items-center gap-4">
            <div className="text-sm text-muted-foreground">
              © {new Date().getFullYear()} InvestedCalculator.online. All rights reserved.
            </div>
            <div className="flex items-center gap-4">
              <a
                href="https://github.com"
                target="_blank"
                rel="noopener noreferrer"
                className="text-muted-foreground hover:text-primary transition-colors"
              >
                <Github className="h-5 w-5" />
              </a>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default Layout;
